<template>
  <div id="app">
    <Toast />
    <ConfirmDialog />

    <div v-if="authStore.isAuthenticated" class="shell-layout">
      <aside v-show="sidebarVisible" class="shell-sidebar">
        <div class="sidebar-brand">
          <div class="brand-badge">H</div>
          <div class="brand-text">
            <strong>HMO</strong>
            <span>RIS IMG</span>
          </div>
          <button class="brand-close" @click="toggleSidebar" aria-label="Fermer le menu">
            <i class="pi pi-times"></i>
          </button>
        </div>

        <div class="sidebar-nav">
          <div class="sidebar-section">
            <template v-for="item in mainItems" :key="isMenuGroup(item) ? item.key : item.route">
              <!-- Groupe déroulant -->
              <div v-if="isMenuGroup(item)" class="sidebar-group">
                <button
                  class="sidebar-item sidebar-group-toggle"
                  :class="{ 'group-active': isGroupActive(item) }"
                  @click="toggleGroup(item.key)"
                >
                  <i :class="item.icon"></i>
                  <span>{{ item.label }}</span>
                  <i
                    class="pi pi-chevron-right sidebar-chevron"
                    :class="{ 'sidebar-chevron--open': openGroups[item.key] }"
                  ></i>
                </button>
                <div v-show="openGroups[item.key]" class="sidebar-children">
                  <button
                    v-for="child in item.children"
                    :key="child.route"
                    class="sidebar-item sidebar-subitem"
                    :class="{ active: isActiveMenu(child) }"
                    @click="handleMenuClick(child)"
                  >
                    <i :class="child.icon"></i>
                    <span>{{ child.label }}</span>
                  </button>
                </div>
              </div>

              <!-- Item simple -->
              <button
                v-else
                class="sidebar-item"
                :class="{ active: isActiveMenu(item) }"
                @click="handleMenuClick(item)"
              >
                <i :class="item.icon"></i>
                <span>{{ item.label }}</span>
              </button>
            </template>
          </div>

          <div class="sidebar-section sidebar-section-bottom">
            <button
              v-for="item in bottomItems"
              :key="item.route"
              class="sidebar-item"
              :class="{ active: isActiveMenu(item) }"
              @click="handleMenuClick(item)"
            >
              <i :class="item.icon"></i>
              <span>{{ item.label }}</span>
            </button>
          </div>
        </div>

        <div
          class="sidebar-user"
          :class="{ active: route.path === '/profile' }"
          role="button"
          tabindex="0"
          @click="goToProfile"
          @keydown.enter="goToProfile"
          @keydown.space.prevent="goToProfile"
        >
          <div class="user-avatar">{{ userInitials }}</div>
          <div class="user-meta">
            <strong>{{ userDisplayName }}</strong>
            <span>{{ userRoleLabel }}</span>
          </div>
          <button class="sidebar-logout" @click.stop="logout" aria-label="Deconnexion">
            <i class="pi pi-sign-out"></i>
          </button>
        </div>
      </aside>

      <section class="shell-main" :class="{ expanded: !sidebarVisible }">
        <header class="shell-topbar">
          <div class="topbar-left">
            <button class="icon-btn" @click="toggleSidebar" aria-label="Basculer le menu">
              <i class="pi pi-bars"></i>
            </button>
            <span class="topbar-brand">HMO</span>
          </div>

          <nav class="topbar-nav">
            <button
              v-for="item in topbarItems"
              :key="item.route"
              class="topbar-nav-item"
              :class="{ active: isActiveMenu(item) }"
              @click="handleMenuClick(item)"
            >
              <i :class="item.icon"></i>
              <span>{{ item.label }}</span>
            </button>
          </nav>

          <div class="topbar-right">
            <button class="icon-btn muted" aria-label="Theme">
              <i class="pi pi-moon"></i>
            </button>
            <div class="notif-wrap">
              <NotificationBadge />
            </div>
          </div>
        </header>

        <main class="shell-content">
          <router-view />
        </main>
      </section>
    </div>

    <div v-else class="o_login_layout">
      <router-view />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useSimpleAuthStore } from '@/stores/simpleAuth'
import { useNotificationStore } from '@/stores/notifications'
import NotificationBadge from '@/components/notifications/NotificationBadge.vue'

const router = useRouter()
const route = useRoute()
const authStore = useSimpleAuthStore()
const notificationStore = useNotificationStore()

const sidebarVisible = ref(false)

// ── Types ──────────────────────────────────────────────────────────────────
type MenuItem = { label: string; icon: string; route: string }
type MenuGroup = { label: string; icon: string; key: string; children: MenuItem[] }
type NavItem = MenuItem | MenuGroup

function isMenuGroup(item: NavItem): item is MenuGroup {
  return 'children' in item
}

// ── État des groupes ouverts ───────────────────────────────────────────────
const openGroups = reactive<Record<string, boolean>>({})

function toggleGroup(key: string) {
  openGroups[key] = !openGroups[key]
}

function isGroupActive(group: MenuGroup): boolean {
  return group.children.some(child => route.path.startsWith(child.route))
}

// ── Navigation principale par rôle ────────────────────────────────────────
const mainItems = computed((): NavItem[] => {
  const role = authStore.user?.role
  const items: NavItem[] = []

  if (role === 'technicien') {
    items.push({ label: 'Mon espace',      icon: 'pi pi-desktop',       route: '/imagerie/technicien' })
    items.push({ label: 'Rendez-vous',     icon: 'pi pi-calendar-plus', route: '/imagerie/rendez-vous' })
    items.push({ label: 'Tableau de bord', icon: 'pi pi-th-large',      route: '/dashboard' })

  } else if (role === 'assistant') {
    items.push({ label: 'Mon espace', icon: 'pi pi-desktop', route: '/imagerie/accueil' })
    if (authStore.hasPermission('patients.can_view_patient')) {
      items.push({ label: 'Patients', icon: 'pi pi-users', route: '/patients' })
    }
    if (authStore.hasPermission('imagerie.can_manage_rdv')) {
      items.push({ label: 'Rendez-vous', icon: 'pi pi-calendar-plus', route: '/imagerie/rendez-vous' })
    }

  } else if (authStore.isMedecinImagerie) {
    items.push({ label: 'Mon espace',      icon: 'pi pi-desktop',       route: '/imagerie/accueil' })
    items.push({ label: 'Rendez-vous',     icon: 'pi pi-calendar-plus', route: '/imagerie/rendez-vous' })
    items.push({ label: 'Tableau de bord', icon: 'pi pi-th-large',      route: '/dashboard' })
    items.push({ label: 'Patients',        icon: 'pi pi-users',         route: '/patients' })
    items.push({
      key: 'comptes-rendus',
      label: 'Comptes-rendus',
      icon: 'pi pi-file-edit',
      children: [
        { label: 'Compte-rendu', icon: 'pi pi-file-edit', route: '/imagerie/radiologue' },
        { label: 'Modèles CR',   icon: 'pi pi-copy',      route: '/imagerie/modeles-cr' },
      ],
    })

  } else if (role === 'medecin') {
    items.push({ label: 'Mon espace',      icon: 'pi pi-desktop',       route: '/imagerie/medecin' })
    items.push({ label: 'Tableau de bord', icon: 'pi pi-th-large',      route: '/dashboard' })
    items.push({ label: 'Rendez-vous',     icon: 'pi pi-calendar-plus', route: '/imagerie/rendez-vous' })
    items.push({ label: 'Patients',        icon: 'pi pi-users',         route: '/patients' })
    items.push({ label: 'Modèles CR',      icon: 'pi pi-copy',          route: '/imagerie/modeles-cr' })

  } else if (role === 'admin') {
    // Items directs
    items.push({ label: 'Mon espace',      icon: 'pi pi-desktop',  route: '/imagerie/accueil' })
    items.push({ label: 'Tableau de bord', icon: 'pi pi-th-large', route: '/dashboard' })

    // Groupe : Activité quotidienne
    items.push({
      key: 'activite',
      label: 'Activité',
      icon: 'pi pi-calendar',
      children: [
        { label: 'Rendez-vous',     icon: 'pi pi-calendar-plus', route: '/imagerie/rendez-vous' },
        { label: 'Salle d\'examen', icon: 'pi pi-desktop',       route: '/imagerie/technicien' },
        { label: 'Examens',         icon: 'pi pi-images',        route: '/imagerie/examens' },
        { label: 'Maint. salles',   icon: 'pi pi-wrench',        route: '/imagerie/maintenance-salles' },
      ],
    })

    // Groupe : Dossiers
    items.push({
      key: 'dossiers',
      label: 'Dossiers',
      icon: 'pi pi-folder',
      children: [
        { label: 'Patients', icon: 'pi pi-users',     route: '/patients' },
        { label: 'Médecins', icon: 'pi pi-user-plus', route: '/medecins' },
      ],
    })

    // Groupe : Comptes-rendus
    items.push({
      key: 'comptes-rendus',
      label: 'Comptes-rendus',
      icon: 'pi pi-book',
      children: [
        { label: 'Compte-rendu',  icon: 'pi pi-file-edit',  route: '/imagerie/radiologue' },
        { label: 'Modèles CR',    icon: 'pi pi-copy',       route: '/imagerie/modeles-cr' },
        { label: 'Transcription', icon: 'pi pi-headphones', route: '/imagerie/transcription' },
      ],
    })
  }

  // Gestion managériale (admin + chef de service)
  if (authStore.isAdmin || authStore.isChefService) {
    items.push({
      key: 'gestion',
      label: 'Gestion',
      icon: 'pi pi-briefcase',
      children: [
        { label: 'Tarifs',       icon: 'pi pi-percentage', route: '/gestion/tarifs' },
        { label: 'Statistiques', icon: 'pi pi-chart-bar',  route: '/gestion/stats' },
        { label: 'Paiements',    icon: 'pi pi-wallet',     route: '/gestion/paiements' },
      ],
    })
  }

  return items
})

// Auto-ouvre le groupe qui contient la route active
watch(
  () => route.path,
  (path) => {
    for (const item of mainItems.value) {
      if (isMenuGroup(item) && item.children.some(c => path.startsWith(c.route))) {
        openGroups[item.key] = true
      }
    }
  },
  { immediate: true }
)

// Éléments toujours en bas
const bottomItems = computed((): MenuItem[] => {
  const items: MenuItem[] = []
  if (authStore.isAdmin || authStore.isChefService) {
    items.push({ label: 'Rapport',        icon: 'pi pi-chart-line', route: '/rapport' })
  }
  if (authStore.isAdmin) {
    items.push({ label: 'Administration', icon: 'pi pi-cog',        route: '/administration' })
  }
  items.push({ label: 'Notifications',   icon: 'pi pi-bell',        route: '/notifications' })
  return items
})

// ── Raccourcis topbar par rôle ─────────────────────────────────────────────
const topbarItems = computed((): MenuItem[] => {
  const role = authStore.user?.role

  if (role === 'assistant') {
    const items: MenuItem[] = [
      { label: 'Mon espace', icon: 'pi pi-desktop', route: '/imagerie/accueil' }
    ]
    if (authStore.hasPermission('patients.can_view_patient')) {
      items.push({ label: 'Patients', icon: 'pi pi-users', route: '/patients' })
    }
    if (authStore.hasPermission('imagerie.can_manage_rdv')) {
      items.push({ label: 'Rendez-vous', icon: 'pi pi-calendar-plus', route: '/imagerie/rendez-vous' })
    }
    return items
  }

  if (role === 'technicien') {
    return [
      { label: 'Mon espace',  icon: 'pi pi-desktop',       route: '/imagerie/technicien' },
      { label: 'Rendez-vous', icon: 'pi pi-calendar-plus', route: '/imagerie/rendez-vous' },
    ]
  }

  if (authStore.isMedecinImagerie) {
    return [
      { label: 'Mon espace', icon: 'pi pi-desktop', route: '/imagerie/accueil' },
    ]
  }

  if (role === 'medecin') {
    return [
      { label: 'Mon espace', icon: 'pi pi-desktop', route: '/imagerie/medecin' },
    ]
  }

  if (role === 'admin') {
    return [
      { label: 'Mon espace',     icon: 'pi pi-desktop',    route: '/imagerie/accueil' },
      { label: 'Patients',       icon: 'pi pi-users',      route: '/patients' },
      { label: 'Rapport',        icon: 'pi pi-chart-line', route: '/rapport' },
      { label: 'Administration', icon: 'pi pi-cog',        route: '/administration' },
    ]
  }

  return [{ label: 'Mon espace', icon: 'pi pi-desktop', route: '/imagerie/accueil' }]
})

// ── Helpers ────────────────────────────────────────────────────────────────
const userDisplayName = computed(() => {
  const first = authStore.user?.first_name || ''
  const last = authStore.user?.last_name || ''
  return `${first} ${last}`.trim() || 'Utilisateur'
})

const userRoleLabel = computed(() => {
  const labels: Record<string, string> = {
    admin: 'Administrateur',
    medecin: 'Médecin',
    technicien: 'Technicien',
    assistant: 'Assistant',
  }
  return labels[authStore.user?.role || ''] || authStore.user?.role || 'Utilisateur'
})

const userInitials = computed(() => {
  const first = authStore.user?.first_name?.charAt(0) || ''
  const last = authStore.user?.last_name?.charAt(0) || ''
  return `${first}${last}`.toUpperCase() || 'US'
})

function toggleSidebar() {
  sidebarVisible.value = !sidebarVisible.value
}

function handleMenuClick(item: MenuItem) {
  router.push(item.route)
}

function isActiveMenu(item: MenuItem) {
  return route.path.startsWith(item.route)
}

function goToProfile() {
  if (route.path !== '/profile') router.push('/profile')
}

async function logout() {
  notificationStore.disconnect()
  await authStore.logout()
  router.push('/login')
}

onMounted(async () => {
  if (authStore.isAuthenticated) {
    const enabled = import.meta.env.VITE_ENABLE_NOTIFICATIONS === 'true'
    if (enabled) {
      try {
        await notificationStore.initialize()
      } catch (error) {
        console.error('Erreur initialisation notifications:', error)
      }
    }
  }
})

onUnmounted(() => {
  if (authStore.isAuthenticated) notificationStore.disconnect()
})
</script>

<style scoped>
.shell-layout {
  height: 100vh;
  display: flex;
  background: #050b27;
  overflow: hidden;
}

.shell-sidebar {
  width: 248px;
  height: 100vh;
  flex-shrink: 0;
  border-right: 1px solid rgba(148, 163, 184, 0.16);
  background: #111a2b;
  padding: 0.9rem;
  display: flex;
  flex-direction: column;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 1rem;
}

.brand-badge {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  font-weight: 800;
  color: #eff6ff;
  background: #2e6cf0;
}

.brand-text {
  display: flex;
  flex-direction: column;
}

.brand-text strong {
  color: #ffffff;
  font-size: 1.03rem;
}

.brand-text span {
  color: #94a3b8;
  font-size: 0.68rem;
  letter-spacing: 0.1em;
}

.brand-close {
  margin-left: auto;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
}

.sidebar-nav {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding-right: 0.15rem;
}

.sidebar-section {
  margin-top: 0.3rem;
}

.sidebar-section-bottom {
  margin-top: auto;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(148, 163, 184, 0.15);
}

/* ── Item standard ── */
.sidebar-item {
  width: 100%;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #cbd5e1;
  padding: 0.65rem 0.75rem;
  margin-bottom: 0.18rem;
  display: flex;
  align-items: center;
  gap: 0.62rem;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s, color 0.12s;
}

.sidebar-item:hover {
  background: rgba(148, 163, 184, 0.14);
  color: #e2e8f0;
}

.sidebar-item.active {
  background: linear-gradient(90deg, #2f6ef0, #3564d3);
  color: #ffffff;
}

/* ── Groupe déroulant ── */
.sidebar-group {
  margin-bottom: 0.18rem;
}

.sidebar-group-toggle {
  margin-bottom: 0;
}

/* Groupe avec enfant actif : teinte légère sans gradient */
.sidebar-group-toggle.group-active {
  color: #93c5fd;
  background: rgba(47, 110, 240, 0.14);
}

.sidebar-group-toggle.group-active:hover {
  background: rgba(47, 110, 240, 0.2);
}

.sidebar-chevron {
  margin-left: auto;
  font-size: 0.7rem;
  opacity: 0.6;
  transition: transform 0.2s ease;
}

.sidebar-chevron--open {
  transform: rotate(90deg);
}

/* ── Sous-items ── */
.sidebar-children {
  padding-left: 0.5rem;
  padding-top: 0.1rem;
  padding-bottom: 0.1rem;
  border-left: 1px solid rgba(148, 163, 184, 0.12);
  margin-left: 0.9rem;
  margin-bottom: 0.18rem;
}

.sidebar-subitem {
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem 0.7rem;
  color: #94a3b8;
  border-radius: 8px;
  margin-bottom: 0.1rem;
}

.sidebar-subitem:hover {
  color: #e2e8f0;
  background: rgba(148, 163, 184, 0.12);
}

.sidebar-subitem.active {
  background: linear-gradient(90deg, #2f6ef0, #3564d3);
  color: #ffffff;
}

/* ── Utilisateur ── */
.sidebar-user {
  margin-top: auto;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  padding-top: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.sidebar-user:hover {
  background: rgba(148, 163, 184, 0.12);
}

.sidebar-user.active {
  background: rgba(47, 110, 240, 0.22);
}

.user-avatar {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  font-weight: 700;
  color: #eff6ff;
  background: #334155;
  flex-shrink: 0;
}

.user-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.user-meta strong {
  color: #ffffff;
  font-size: 0.95rem;
  line-height: 1.1;
}

.user-meta span {
  color: #94a3b8;
  font-size: 0.72rem;
}

.sidebar-logout {
  margin-left: auto;
  border: none;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
}

/* ── Shell ── */
.shell-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100vh;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.shell-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
  background: #101a36;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 14px;
  padding: 0.5rem 0.7rem;
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-shrink: 0;
}

.topbar-brand {
  font-weight: 800;
  font-size: 1rem;
  color: #ffffff;
  letter-spacing: 0.06em;
}

.topbar-nav {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  flex: 1;
  justify-content: center;
}

.topbar-nav-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.42rem 0.85rem;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #94a3b8;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}

.topbar-nav-item:hover {
  background: rgba(148, 163, 184, 0.14);
  color: #dbe6ff;
}

.topbar-nav-item.active {
  background: linear-gradient(90deg, #2f6ef0, #3564d3);
  color: #ffffff;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: #1a284a;
  color: #cbd5e1;
  display: grid;
  place-items: center;
  cursor: pointer;
}

.icon-btn.muted {
  color: #94a3b8;
}

.notif-wrap {
  min-width: 36px;
  min-height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: #1a284a;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem;
}

.shell-content {
  margin-top: 0.7rem;
  flex: 1;
  min-height: 0;
  overflow: auto;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: #07122f;
  padding: 0.85rem;
}

.o_login_layout {
  min-height: 100vh;
}

@media (max-width: 980px) {
  .topbar-nav-item span { display: none; }
}

@media (max-width: 760px) {
  .shell-layout {
    height: auto;
    min-height: 100vh;
    flex-direction: column;
    overflow: visible;
  }
  .shell-sidebar { width: 100%; height: auto; }
  .shell-main { height: auto; overflow: visible; }
  .shell-content { overflow: visible; }
}
</style>
